import feelgood from '../../assets/feel-good-productivity.jpg';

const feelGoodProductivity = {
  slug: "feel-good-productivity",
  title: "Feel Good Productivity",
  author: "Ali Abdaal",
  cover: feelgood,
  dateRead: "June 2025",
  categories: ["Productivity", "Life"],
  mediumLink: "https://medium.com/@Loubna-DS/on-feel-good-productivity-7cbfcb5d5b32",

  contentSections: [
    {
      id: "why-i-read",
      title: "📖 Why I Read This Book",
      content: [
        {
          type: "markdown",
          value: `

I’ve always wanted to close the gap between *wanting* to do things and actually *getting them done*. The idea of working smarter, not harder, is something I’ve long wanted to embody — but deep down, I didn’t fully believe in it.  

Most theories around discipline and achievement seemed to scream “grind harder,” and none of them really worked for me.  

That started to shift when I came across [Ali’s videos](https://www.youtube.com/@AliAbdaal). I began watching, taking notes, and noticing small but real changes in my mindset — and, eventually, in my actions.  

So reading his book, *Feel-Good Productivity*, felt like the next natural step.  
And honestly? It was definitely worth it.

          `
        }
      ]
    },
    {
      id: "three-words",
      title: "🧠 The Book in 3 Words",
      content: [
        {
          type: "markdown",
          value: `
**Productive. Joyful. Smart.**  
*It reminded me that getting things done doesn’t have to feel heavy — it can feel good, light, and still be deeply effective.*
          `
        }
      ]
    },
    {
      id: "one-insight",
      title: "💡 One Insight That Shifted Me",
      content: [
        {
          type: "markdown",
          value: `
**Enjoyment is efficiency.**  
Every task can be sliced, softened, and made at least 5% more enjoyable. That small shift — from *“I have to”* to *“I want to”* — is where momentum starts.  

And even though I kind of already knew this, the book gave me a way to name it, to **intentionally** ask: *How can I make this more fun?*
          `
        }
      ]
    },
    {
      id: "favorite-quotes",
      title: "❝ My Favorite Quotes",
      content: [
        {
          type: "markdown",
          value: `
> *“A shared joy is double joy. A shared sorrow is half sorrow.”*  

> *“By doing less today, you can do more of what matters tomorrow.”*  

> *“When we think about death, we get a clearer view of life.”*
          `
        }
      ]
    },
    {
      id: "summary-notes",
      title: "🧾 Summary & Notes",
      content: [
        {
          type: "markdown",
          value: `
**Feeling good isn’t just about feeling good.**

We often convince ourselves that our energy is limited — and to some extent, that’s true. But the moment our mood shifts, everything changes. We feel lighter, more open, more willing. Suddenly, we have more **energy** — not because we rested, but because we *felt good*.

![Feeling good generates energy.](/book-images/feelgood.png)

**→ Feeling good generates energy.** It’s a tool for productivity and creativity.

### 🔋Energize

🎮 **Energizer 1: Play**

One of the first things we’re told when we grow up is to **stop playing** and take life more seriously.
But seriousness often leads to stress. And stress blocks creativity.

The truth is: we need more *play* in our daily lives — not just as a break from work, but as a way to approach it.

- The first ingredient of play is **adventure**.
- Embrace **curiosity**. Add a side quest to your day to elevate it.
- We should seek **fun** everywhere we go.

> *What this looks like if it was more fun ?*

- Enjoy the process — not just the outcome.
- Make your environment more relaxed to reduce stress.
- It’s about how you frame your failure.

**Process of collecting data points**

Failure is not really a pleasurable experience — but it’s how we view it that shapes the experience. If you go through a failure, try to see it as a **set point** — a moment to ask questions, gain insight, and learn something.
That’s what will eventually guide you to know what to do next.


![Feeling good generates energy.](/book-images/feelggod2.png)
- Be less serious, be sincere.

> *How can I approach the work with less seriousness?*

**⚡Energizer 2: Power**

Believing you can is the first step. If they can do it, *you* can too.
The moment you start to feel more powerful — more in control — you naturally become more confident, more energized, more ready to get things done.

- Learning through doing is the most powerful forces in human psychology.
- Have a beginner Mindset.
- Gain Power from teaching what you’ve learned.
- Power of ownership , own the process.
- ‘ Have to’ is a powerless language, instead of, ‘Choose to’, give you more power.

The truth is, you don’t actually have to do anything. But you do need to accept the responsibility for what happens if you don’t.

> *“Why do I have to do this?”*

**🫂 Energizer 3: People**

Ever felt more energized after hanging out with someone — and completely drained after being with someone else?

That’s because people *are* energy. And when it comes to relationships, we’re surrounded by two types: the **energizers** and the **drainers**.

Our job is to intentionally **spend more time with the energizers** — the ones who fuel us, not deplete us.

- Shift from a **competition** mindset to a **comrade** mindset.
- Team work is much as **psychological** state as a way for dividing up tasks.
- Practice kindness. Ask for help. Give help. Receive it, too.

We often assume we’ve said enough — but the truth is, we tend to **under-communicate** the good stuff.

Say it again. Say it clearly. Let people know what they’re doing right.
> *“Is there a piece of information you’re hoarding that might just make someone else’s week?”*

### 🔓 Unblock
There are usually two paths we take when trying to get things done:
The **motivation method** — we do it because we feel like it.
And the **discipline method** — we do it anyway, even if we don’t feel like it.

But Ali introduces a third path: the **Unblock method**.

Instead of forcing ourselves through, we pause to ask:

> *Why am I resisting this? What’s the feeling behind my procrastination?*

The Unblock method is about identifying the **root cause** — the emotional block, the story, the discomfort — that’s stopping us from starting.
It’s not about pushing through. It’s about *removing what’s in the way*.


**🔍 Blocker 1: Not Seeking Clarity**

Remove the fog of uncertainty.

You have to understand **why** you’re doing what you want to do.

Start by asking *why?* — then ask it again. Ask it five times if needed.
Each answer should pull you closer to your purpose. Your goals and projects should feel aligned, clear, and **quantifiable**.

Ask *what?* — and make your goals **nice**, not just smart.
Nice = **Near-term, Impact-based, Controllable, and Energizing**.

Instead of:
→ *“Lose 20 pounds in the next 3 months.”*

Try:
→ *“Exercise for 30 minutes daily, doing activities I enjoy and can stick to.”*

Ask *when?* — and use **Time Blocking** to protect your focus:

- **Level 1**: Block 1–2 hours
- **Level 2**: Block a full day
- **Level 3**: Block your entire week

![Feeling good generates energy.](/book-images/timeblocking2.png)

**🌪️ Blocker 2: Fear**

Know your fear. Put your feelings into words.
Naming what we feel can shrink its power — this is known as the **labeling effect**, and it works both ways: positive and negative.


Ali shares the **10/10/10 Rule**:
Ask yourself, *“Will this matter in 10 minutes? 10 weeks? 10 years?”*
It’s a simple way to step back and regain perspective.


A lot of our fear isn’t about the task — it’s about being *seen*.
But the truth is: no one’s watching as closely as we think.
We need to stop **spotlighting** ourselves. Most people are too caught up in their own lives to judge us the way we imagine.


And if you’re still hesitating, try **the Batman Effect**:

Choose a character you admire — a superhero, a role model, even a version of your future self — and pretend to be them while doing the task.
Borrow their mindset. Step into the moment *as them*.

Sometimes the fear fades when you stop being you — just for a little while.

**🧊 Blocker 3: Doing Nothing**

Isaac Newton’s First Law of Motion — the law of inertia — says:

> *“An object at rest stays at rest, while an object in motion stays in motion unless acted on by external imbalanced force.”*

In productivity language: if you do nothing, it’s easier for you to keep on doing nothing, until another force energy comes up and change the behaviors.


Getting started is the hardest part.
But once you begin, even just a little, momentum starts to build.


There are two kinds of restrictions that stop us from taking action:

- **Physical restrictions** — your environment may not be helping you. Set things up so the action feels obvious and frictionless.
- **Emotional restrictions** — use the **5-minute rule**. Tell yourself you’ll do the task for just five minutes, then stop. Ironically, you’ll likely want to continue.


Track your progress — so you can see you’re moving forward.
Focus on the **small wins**.

Find an **accountability buddy**.

And above all: **forgive yourself** when it doesn’t go as planned.


Progress is built from motion. But motion begins with one small choice.

### 🌱 Sustain
**🪫Burnout type1: Overexertion**

Overexertion happens when you overcommit, overdo, and overinvest all your energy.

You’re constantly running, constantly giving — until you hit empty.
- The solution? It’s simple but hard: **do less**.

> *“Do less, so that you can unlock more.”*

Learn to say *no*. If it’s not a *Hell Yes*, it’s a No.

Before saying yes to anything, pause and ask yourself:

> *“Would I be excited about this commitment if it were happening tomorrow?”*

When you fall off track — don’t abandon the whole course.

**BEGIN AGAIN**.

Take breaks. Find moments to do nothing. And embrace them.
Rest isn’t optional — it’s a necessity.

Try saying:
> *“I’ll take a break every ____. I’ll actually take it.”*

**🥀Burnout type2: Depletion**

Depletion doesn’t just come from doing too much — it comes from **recharging the wrong way**.

We think we’re resting, but often we’re just draining ourselves in different ways.

A common example? Scrolling on social media. It feels like rest, but it leaves us more tired than before.

> *We need to learn how to recharge the right way*.

![Alt text goes here](/book-images/recharge.png)

Start by recharging **creatively** — through activities that are *CALM*:

→ **C**ompetence

→ **A**utonomy

→ **L**iberty

→ **M**ellow energy

- Let it be playful. Don’t turn your hobbies into work.
Let rest be light and free of goals.

Also, recharge **mindlessly**.

Even your hobbies may require mental energy — so sometimes, it’s better to just let your mind wander.

- Take long showers.
- Enjoy a bath.
- Do nothing at all.
- Do your chores in silence
- Let your thoughts drift

You don’t always need stimulation.
Sometimes, the most powerful rest is *emptiness*.

**🧭Burnout type3: Misalignment**

Sometimes burnout doesn’t come from **doing too much** or **recharging the wrong way**.

Sometimes, it comes from this:

>*Your goals no longer align with your sense of self*.

The fix isn’t more effort — it’s **realignment**.

You need to bring your **daily actions** closer to your **deeper values**.

**Long-Term Horizon**

Let death remind you of what truly matters.

Ask yourself:

- If I continue on my current path, what does my life look like 5 years from now?
- What if I took an alternative path?
- What if I followed a radical path — one where money, obligations, and others’ opinions didn’t matter at all?

**Wheel of Life**

This tool helps you reflect on each key area of your life, give each aspect a ranking to gain clarity about where you are.
![Feeling good generates energy.](/book-images/wheell.png)
**12-Month Celebration**

Imagine it’s 12 months from now and everything has gone right.
You’re celebrating the life you really wanted.

Now ask:

> *What do I need to do this year to make that celebration real?*

**The Variable Shift**

If you could change just one **independent variable** in your life — one habit, one system — that would ripple across every other area…

What would it be?

For me personally, it’s **waking up early.**

That one shift affects my energy, my time, how I show up — and it changes everything else with it.

---
## Final Thought

Ali ends the book by encouraging us to think like **productivity scientists**.
Because what works for one person may not work for another — and that’s okay.

It’s all about **experimentation**.

We need to try things, test techniques, and gently observe what fits *us* best.

Personally, I’ve been applying this **trial-and-error** approach in my own life, trying out different tools and noticing how they affect my performance.

One productivity insight that’s helped me the most is this:

> *Work with your energy, not against it.*

As a software developer, I often need long blocks of focus time.
But I’ve learned not to force deep work when my energy is low.
Instead, I shift to lighter, more digestible tasks during those moments —
and save the heavy lifting for when I feel fully present and charged.

In the end, productivity isn’t a system — it’s a relationship.
And like any relationship, it takes time, listening, and care.
`
        }
      ]
    }
  ]
};

export default feelGoodProductivity;
